import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ChevronRight, Download, Search, X } from 'lucide-react';
import clsx from 'clsx';
import { get, post } from '../lib/api';
import { Button, ErrorNote, Loading } from '../ui';
import { MirrorChart } from '../components/MirrorChart';
import { GRADE_MEANING, GRADE_STYLE, GradeBadge, Scorecard, type Grade } from '../components/Scorecard';

/**
 * The whole Google Ads account, read-only, laid out the way Google Ads lays it
 * out: sections down the left, a date range at the top, campaign → ad group →
 * keywords by clicking through, and every setting of any row one click away.
 *
 * It shows a SNAPSHOT — what Google said for one window, at one moment — and
 * says which, always. A copy that looks live and is a week old is how a
 * client gets told something that stopped being true on Tuesday.
 */

type SectionInfo = { key: string; label: string; group: string; hidden: boolean; note: string | null };
type SectionState = { key: string; rows: number; structure_error: string | null; metrics_error: string | null };
type Snapshot = {
  id: string; date_from: string; date_to: string; started_at: string; finished_at: string | null;
  ok: boolean | null; sections: SectionState[]; error: string | null;
};
type Column = {
  key: string; label: string; type: 'text' | 'number' | 'money' | 'percent' | 'list' | 'bool' | 'datetime' | 'grade' | 'ratio';
  from: string; wide?: boolean; total?: boolean;
};
type Row = Record<string, unknown> & { id: string; name: string | null; metrics_known: boolean };
type Page = {
  section: { key: string; label: string; note: string | null; metrics_only: boolean };
  state: SectionState | null; columns: Column[]; sort: string; dir: 'asc' | 'desc';
  total: number; metrics_unknown: number; totals: Record<string, number | null>; rows: Row[];
  grades: { grade: Grade | null; n: number }[];
};

const PAGE = 100;
const METRIC_COLS = new Set(['impressions', 'clicks', 'ctr', 'avg_cpc', 'cost', 'conversions', 'conv_rate', 'cpa', 'conv_value', 'all_conversions', 'search_is', 'lost_is_budget', 'lost_is_rank']);

// ── dates ────────────────────────────────────────────────────────────────────

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function presets() {
  const today = new Date();
  const yesterday = addDays(today, -1);
  const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastOfLast = addDays(firstThis, -1);
  return [
    { label: 'Last 7 days', from: iso(addDays(yesterday, -6)), to: iso(yesterday) },
    { label: 'Last 30 days', from: iso(addDays(yesterday, -29)), to: iso(yesterday) },
    { label: 'Last 90 days', from: iso(addDays(yesterday, -89)), to: iso(yesterday) },
    { label: 'This month', from: iso(firstThis), to: iso(today) },
    { label: 'Last month', from: iso(firstLast), to: iso(lastOfLast) },
  ];
}

const fmtDay = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const when = (t: string) => new Date(t).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// ── formatting ───────────────────────────────────────────────────────────────

export function fmt(value: unknown, type: Column['type'], currency: string): string {
  if (value == null || value === '') return '—';
  switch (type) {
    case 'money': return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: Number(value) < 100 ? 2 : 0 }).format(Number(value));
    case 'number': { const n = Number(value); return Number.isInteger(n) ? n.toLocaleString('en-IN') : n.toLocaleString('en-IN', { maximumFractionDigits: 2 }); }
    case 'percent': return `${(Number(value) * 100).toFixed(Number(value) < 0.1 ? 2 : 1)}%`;
    case 'bool': return value ? 'Yes' : 'No';
    case 'list': return Array.isArray(value) ? (value.length ? value.join(', ') : '—') : String(value);
    case 'ratio': return `×${Number(value).toFixed(2)}`;
    case 'datetime': return new Date(String(value).replace(' ', 'T')).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    default: return String(value);
  }
}

const humanise = (k: string) => k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// ── the page ─────────────────────────────────────────────────────────────────

export default function Mirror() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v == null ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: false });
  };

  const account = useQuery({
    queryKey: ['account', id],
    queryFn: () => get<{ account: { id: string; name: string; currency: string; customer_id: string } }>(`/v1/accounts/${id}`),
  });
  const list = useQuery({
    queryKey: ['mirror', id],
    queryFn: () => get<{ sections: SectionInfo[]; snapshots: Snapshot[] }>(`/v1/accounts/${id}/mirror`),
  });

  const snapshots = list.data?.snapshots ?? [];
  const running = snapshots.find((s) => !s.finished_at) ?? null;
  const chosen = snapshots.find((s) => s.id === params.get('snap'))
    ?? snapshots.find((s) => s.finished_at && !s.error) ?? null;

  // While a pull runs, ask about it until it is done, then show it.
  const poll = useQuery({
    queryKey: ['mirror-snap', running?.id],
    queryFn: () => get<Snapshot>(`/v1/mirror/${running!.id}`),
    enabled: !!running,
    refetchInterval: (q) => (q.state.data?.finished_at ? false : 1500),
  });
  useEffect(() => {
    if (poll.data?.finished_at) {
      qc.invalidateQueries({ queryKey: ['mirror', id] });
      if (!poll.data.error) set({ snap: poll.data.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.data?.finished_at]);

  const [range, setRange] = useState(() => presets()[1]);
  const pull = useMutation({
    mutationFn: (w: { from: string; to: string }) => post<Snapshot>(`/v1/accounts/${id}/mirror`, w),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mirror', id] }),
  });

  const section = params.get('s') ?? 'overview';
  const filter = { cid: params.get('cid'), cname: params.get('cname'), gid: params.get('gid'), gname: params.get('gname') };
  const currency = account.data?.account.currency ?? 'INR';

  if (account.isLoading || list.isLoading) return <Loading />;
  if (account.error || list.error) return <ErrorNote error={account.error ?? list.error} />;

  const sections = (list.data?.sections ?? []).filter((s) => !s.hidden);
  const groups = [...new Set(sections.map((s) => s.group))];
  const stateOf = (k: string) => chosen?.sections.find((x) => x.key === k) ?? null;

  return (
    <div>
      <Link to={`/accounts/${id}`} className="mb-3 inline-flex items-center gap-1.5 text-sm text-t2 hover:text-t1">
        <ArrowLeft className="size-4" /> {account.data?.account.name}
      </Link>

      {/* ── the window ──────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-card border border-bd bg-bg2 p-4">
        <div className="mr-auto">
          <h1 className="text-xl font-bold tracking-[-0.02em]">{account.data?.account.name}</h1>
          <p className="text-sm text-t2">
            {chosen
              ? <>Showing <b className="font-medium text-t1">{fmtDay(chosen.date_from)} – {fmtDay(chosen.date_to)}</b>, as Google reported it {when(chosen.finished_at!)}. Read-only.</>
              : 'A read-only copy of the whole account. Pick a window and pull it.'}
          </p>
        </div>

        {snapshots.filter((s) => s.finished_at && !s.error).length > 1 && (
          <label className="text-xs text-t2">
            <span className="mb-1 block">Pulled windows</span>
            <select
              value={chosen?.id ?? ''} onChange={(e) => set({ snap: e.target.value })}
              className="h-9 rounded-btn border border-bd bg-bg px-2 text-sm text-t1"
            >
              {snapshots.filter((s) => s.finished_at && !s.error).map((s) => (
                <option key={s.id} value={s.id}>{fmtDay(s.date_from)} – {fmtDay(s.date_to)}</option>
              ))}
            </select>
          </label>
        )}

        <label className="text-xs text-t2">
          <span className="mb-1 block">Window</span>
          <select
            value={presets().find((p) => p.from === range.from && p.to === range.to)?.label ?? 'Custom'}
            onChange={(e) => { const p = presets().find((x) => x.label === e.target.value); if (p) setRange(p); }}
            className="h-9 rounded-btn border border-bd bg-bg px-2 text-sm text-t1"
          >
            {presets().map((p) => <option key={p.label}>{p.label}</option>)}
            <option disabled>Custom</option>
          </select>
        </label>
        <label className="text-xs text-t2">
          <span className="mb-1 block">From</span>
          <input type="date" value={range.from} max={range.to}
            onChange={(e) => setRange({ label: 'Custom', from: e.target.value, to: range.to })}
            className="h-9 rounded-btn border border-bd bg-bg px-2 text-sm text-t1" />
        </label>
        <label className="text-xs text-t2">
          <span className="mb-1 block">To</span>
          <input type="date" value={range.to} min={range.from} max={iso(new Date())}
            onChange={(e) => setRange({ label: 'Custom', from: range.from, to: e.target.value })}
            className="h-9 rounded-btn border border-bd bg-bg px-2 text-sm text-t1" />
        </label>
        <Button variant="primary" loading={pull.isPending || !!running} onClick={() => pull.mutate({ from: range.from, to: range.to })}>
          <Download className="size-4" />{running ? 'Pulling…' : 'Pull this window'}
        </Button>
      </div>

      <ErrorNote error={pull.error} />
      {running && (
        <p className="mb-4 rounded-btn border border-bd bg-bg3 px-3 py-2 text-sm text-t2">
          Reading the whole account from Google for {fmtDay(running.date_from)} – {fmtDay(running.date_to)}.
          About twenty questions to Google; a big account takes a minute. {poll.data?.sections.length
            ? `${poll.data.sections.length} sections read so far.` : ''}
        </p>
      )}
      {snapshots[0]?.error && snapshots[0].finished_at && (
        <div className="mb-4"><ErrorNote error={new Error(`The last pull did not work: ${snapshots[0].error}`)} /></div>
      )}

      {!chosen ? (
        !running && <p className="rounded-card border border-dashed border-bd2 p-10 text-center text-sm text-t3">
          Nothing pulled yet. Choose a window above and press <b>Pull this window</b>.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
          {/* ── sections, the way Google Ads lists them ─────────────────── */}
          <select
            aria-label="Section" value={section} onChange={(e) => set({ s: e.target.value === 'overview' ? null : e.target.value })}
            className="h-10 rounded-btn border border-bd bg-bg2 px-3 text-sm lg:hidden"
          >
            <option value="overview">Overview</option>
            <option value="score">Scorecard</option>
            {groups.map((g) => (
              <optgroup key={g} label={g}>
                {sections.filter((s) => s.group === g).map((s) => {
                  const st = stateOf(s.key);
                  return <option key={s.key} value={s.key}>{s.label} ({st?.rows ?? 0}){st?.structure_error || st?.metrics_error ? ' ⚠' : ''}</option>;
                })}
              </optgroup>
            ))}
          </select>
          <nav className="hidden rounded-card border border-bd bg-bg2 p-2 text-sm lg:block lg:self-start">
            <NavItem active={section === 'overview'} onClick={() => set({ s: null })} label="Overview" />
            <NavItem active={section === 'score'} onClick={() => set({ s: 'score', grade: null })} label="Scorecard" />
            {groups.map((g) => (
              <div key={g} className="mt-2">
                <div className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-t3">{g}</div>
                {sections.filter((s) => s.group === g).map((s) => {
                  const st = stateOf(s.key);
                  return (
                    <NavItem key={s.key} active={section === s.key} onClick={() => set({ s: s.key })}
                      label={s.label} count={st?.rows}
                      warn={!!(st?.structure_error || st?.metrics_error)} />
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="min-w-0">
            {(filter.cid || filter.gid) && (
              <div className="mb-3 flex flex-wrap items-center gap-1.5 text-sm">
                <button className="text-t2 hover:text-t1" onClick={() => set({ cid: null, cname: null, gid: null, gname: null })}>All campaigns</button>
                {filter.cid && <><ChevronRight className="size-3.5 text-t3" />
                  <button className="font-medium hover:underline" onClick={() => set({ gid: null, gname: null, s: 'ad_groups' })}>{filter.cname}</button></>}
                {filter.gid && <><ChevronRight className="size-3.5 text-t3" /><span className="font-medium">{filter.gname}</span></>}
                <button title="Clear" className="ml-1 text-t3 hover:text-t1" onClick={() => set({ cid: null, cname: null, gid: null, gname: null })}>
                  <X className="size-3.5" />
                </button>
              </div>
            )}

            {section === 'score'
              ? <Scorecard snapshotId={chosen.id} currency={currency}
                  onOpen={(k, g) => set({ s: k, grade: g ?? null, cid: null, cname: null, gid: null, gname: null })} />
              : section === 'overview'
              ? <Overview snap={chosen} currency={currency} sections={list.data?.sections ?? []} onOpen={(k) => set({ s: k })} />
              : <SectionTable key={`${chosen.id}-${section}-${filter.cid}-${filter.gid}`}
                  snap={chosen} section={section} currency={currency} filter={filter}
                  grade={(params.get('grade') as Grade | null) ?? null}
                  onGrade={(g) => set({ grade: g })}
                  onDrill={(level, row) => level === 'campaign'
                    ? set({ cid: String(row.campaign_id), cname: String(row.name ?? ''), gid: null, gname: null, s: 'ad_groups' })
                    : set({ gid: String(row.id), gname: String(row.name ?? ''), cid: String(row.campaign_id ?? ''), cname: String(row.campaign ?? ''), s: 'keywords' })} />}
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({ active, onClick, label, count, warn }: { active: boolean; onClick: () => void; label: string; count?: number; warn?: boolean }) {
  return (
    <button onClick={onClick}
      className={clsx('flex w-full items-center gap-2 rounded-btn px-2 py-1.5 text-left',
        active ? 'bg-ink font-medium text-on-ink' : 'text-t2 hover:bg-hover hover:text-t1')}>
      <span className="truncate">{label}</span>
      {warn && <AlertTriangle className={clsx('size-3.5 shrink-0', active ? 'text-on-ink' : 'text-warn')} aria-label="Partly read" />}
      {count != null && <span className={clsx('num ml-auto text-xs', active ? 'text-on-ink/70' : 'text-t3')}>{count.toLocaleString('en-IN')}</span>}
    </button>
  );
}

// ── overview ─────────────────────────────────────────────────────────────────

const CHART_METRICS = [
  { key: 'cost', label: 'Cost', type: 'money' as const },
  { key: 'clicks', label: 'Clicks', type: 'number' as const },
  { key: 'impressions', label: 'Impressions', type: 'number' as const },
  { key: 'conversions', label: 'Conversions', type: 'number' as const },
];

function Overview({ snap, currency, sections, onOpen }: {
  snap: Snapshot; currency: string; sections: SectionInfo[]; onOpen: (key: string) => void;
}) {
  const acct = useQuery({ queryKey: ['mirror-rows', snap.id, 'account'], queryFn: () => get<Page>(`/v1/mirror/${snap.id}/account`) });
  const daily = useQuery({
    queryKey: ['mirror-rows', snap.id, 'daily'],
    queryFn: () => get<Page>(`/v1/mirror/${snap.id}/daily?sort=name&dir=asc&limit=1000`),
  });
  const [metric, setMetric] = useState(CHART_METRICS[0]);

  const a = acct.data?.rows[0];
  const t = acct.data?.totals ?? {};
  const tiles: [string, unknown, Column['type']][] = [
    ['Cost', t.cost, 'money'], ['Clicks', t.clicks, 'number'], ['Impressions', t.impressions, 'number'],
    ['CTR', t.ctr, 'percent'], ['Avg. CPC', t.avg_cpc, 'money'], ['Conversions', t.conversions, 'number'],
    ['Cost / conv.', t.cpa, 'money'], ['Conv. value', t.conv_value, 'money'],
  ];

  // Every day of the window, including the ones Google had nothing for —
  // those are zero days, not missing ones, when the daily query answered.
  const points = useMemo(() => {
    if (!daily.data) return [];
    const byDay = new Map(daily.data.rows.map((r) => [String(r.name), r]));
    const out: { day: string; value: number | null }[] = [];
    const answered = !daily.data.state?.metrics_error;
    for (let d = new Date(`${snap.date_from}T00:00:00`); iso(d) <= snap.date_to; d = addDays(d, 1)) {
      const r = byDay.get(iso(d));
      out.push({ day: iso(d), value: r ? Number(r[metric.key] ?? 0) : answered ? 0 : null });
    }
    return out;
  }, [daily.data, metric, snap]);

  const problems = snap.sections.filter((s) => s.structure_error || s.metrics_error);
  const label = (k: string) => sections.find((s) => s.key === k)?.label ?? k;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiles.map(([name, v, type]) => (
          <div key={name} className="rounded-card border border-bd bg-bg2 p-3">
            <div className="text-xs text-t3">{name}</div>
            <div className="num mt-1 text-lg font-bold">{acct.isLoading ? '…' : fmt(v, type, currency)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-card border border-bd bg-bg2 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="mr-auto text-sm font-bold">{metric.label} by day</h2>
          {CHART_METRICS.map((m) => (
            <button key={m.key} onClick={() => setMetric(m)}
              className={clsx('rounded-chip px-2.5 py-1 text-xs', metric.key === m.key ? 'bg-ink text-on-ink' : 'bg-bg3 text-t2 hover:text-t1')}>
              {m.label}
            </button>
          ))}
        </div>
        {daily.isLoading ? <Loading /> : (
          <MirrorChart points={points} label={metric.label} format={(v) => fmt(v, metric.type, currency)} />
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-card border border-bd bg-bg2 p-4">
          <h2 className="mb-2 text-sm font-bold">Account settings</h2>
          {a ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              {([
                ['Customer ID', a.id], ['Status', a.status], ['Currency', a.currency], ['Time zone', a.time_zone],
                ['Auto-tagging', a.auto_tagging == null ? null : a.auto_tagging ? 'On' : 'Off'],
                ['Optimisation score', a.optimization_score == null ? null : fmt(a.optimization_score, 'percent', currency)],
                ['Tracking template', a.tracking_template], ['Final URL suffix', a.final_url_suffix],
              ] as [string, unknown][]).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-t3">{k}</dt><dd className="truncate" title={String(v ?? '')}>{v == null || v === '' ? '—' : String(v)}</dd>
                </div>
              ))}
            </dl>
          ) : <p className="text-sm text-t3">Not read.</p>}
        </div>

        <div className="rounded-card border border-bd bg-bg2 p-4">
          <h2 className="mb-2 text-sm font-bold">What this copy contains</h2>
          {problems.length === 0
            ? <p className="text-sm text-t2">Every section was read in full.</p>
            : (
              <ul className="space-y-2 text-sm">
                {problems.map((p) => (
                  <li key={p.key}>
                    <button onClick={() => onOpen(p.key)} className="font-medium hover:underline">{label(p.key)}</button>
                    <span className="text-t2"> — {p.structure_error ? 'could not be read' : 'read, but without its numbers'}: </span>
                    <span className="text-t3">{p.structure_error ?? p.metrics_error}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  );
}

// ── one section as a table ───────────────────────────────────────────────────

function SectionTable({ snap, section, currency, filter, onDrill, grade, onGrade }: {
  snap: Snapshot; section: string; currency: string;
  filter: { cid: string | null; gid: string | null };
  grade: Grade | null; onGrade: (g: Grade | null) => void;
  onDrill: (level: 'campaign' | 'ad_group', row: Row) => void;
}) {
  const [sort, setSort] = useState<string | undefined>();
  const [dir, setDir] = useState<'asc' | 'desc' | undefined>();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<Row | null>(null);

  const query = useQuery({
    queryKey: ['mirror-rows', snap.id, section, filter.cid, filter.gid, sort, dir, q, page, grade],
    queryFn: () => get<Page>(`/v1/mirror/${snap.id}/${section}?` + new URLSearchParams({
      ...(filter.cid ? { campaign_id: filter.cid } : {}),
      ...(filter.gid ? { ad_group_id: filter.gid } : {}),
      ...(sort ? { sort } : {}), ...(dir ? { dir } : {}), ...(q ? { q } : {}), ...(grade ? { grade } : {}),
      limit: String(PAGE), offset: String(page * PAGE),
    })),
    placeholderData: keepPreviousData,
  });
  const d = query.data;
  const pages = d ? Math.ceil(d.total / PAGE) : 0;
  const drill = section === 'campaigns' ? 'campaign' : section === 'ad_groups' ? 'ad_group' : null;

  function clickHeader(key: string) {
    if (d?.sort === key) setDir(d.dir === 'desc' ? 'asc' : 'desc');
    else { setSort(key); setDir(undefined); }
    setPage(0);
  }

  return (
    <div className="rounded-card border border-bd bg-bg2">
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-4 py-3">
        <h2 className="mr-1 text-base font-bold">{d?.section.label ?? '…'}</h2>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-t3" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search"
            className="h-8 w-52 rounded-btn border border-bd bg-bg pl-8 pr-3 text-sm outline-none placeholder:text-t3 focus:border-bd2" />
        </div>
        <span className="num text-xs text-t3">{d ? `${d.total.toLocaleString('en-IN')} ${d.total === 1 ? 'row' : 'rows'}` : ''}</span>
        {pages > 1 && (
          <div className="ml-auto flex items-center gap-2 text-xs">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-btn border border-bd px-2 py-1 disabled:opacity-40">Back</button>
            <span className="num text-t3">{page + 1} / {pages}</span>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-btn border border-bd px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {d && d.grades.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-bd px-4 py-2">
          <button onClick={() => { onGrade(null); setPage(0); }}
            className={clsx('rounded-chip px-2 py-0.5 text-xs', !grade ? 'bg-ink text-on-ink' : 'bg-bg3 text-t2 hover:text-t1')}>All</button>
          {(['Bleeding', 'Costly', 'Watch', 'Good', 'Star', 'Too early', 'Idle', 'Unmeasured', 'Unknown'] as Grade[])
            .map((g) => ({ g, n: d.grades.find((x) => x.grade === g)?.n ?? 0 }))
            .filter((x) => x.n > 0)
            .map(({ g, n }) => (
              <button key={g} title={GRADE_MEANING[g]} onClick={() => { onGrade(grade === g ? null : g); setPage(0); }}
                className={clsx('rounded-chip px-2 py-0.5 text-xs font-medium', GRADE_STYLE[g], grade === g && 'ring-2 ring-ink ring-offset-1 ring-offset-bg2')}>
                {g} <span className="num">{n}</span>
              </button>
            ))}
        </div>
      )}
      {d?.section.note && <p className="border-b border-bd px-4 py-2 text-xs text-t3">{d.section.note}</p>}
      {d?.state?.structure_error && (
        <p className="flex gap-2 border-b border-bd bg-warn-bg px-4 py-2 text-xs text-warn">
          <AlertTriangle className="size-3.5 shrink-0" />Google did not return this section in full: {d.state.structure_error}
        </p>
      )}
      {d?.state?.metrics_error && (
        <p className="flex gap-2 border-b border-bd bg-warn-bg px-4 py-2 text-xs text-warn">
          <AlertTriangle className="size-3.5 shrink-0" />
          The numbers could not be read, so they show as — (not known), never as zero: {d.state.metrics_error}
        </p>
      )}

      {query.isLoading || !d ? <div className="px-4"><Loading /></div>
        : !d.rows.length ? (
          <p className="px-4 py-8 text-center text-sm text-t3">
            {q ? 'Nothing matches that.'
              : d.state?.structure_error || (d.state?.metrics_error && d.section.metrics_only)
                ? 'Nothing could be read for this section — the reason is above. This is not the same as the account having none.'
                : filter.cid || filter.gid ? 'Nothing here for this campaign or ad group.' : 'Google has nothing in this section for this account.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg3 text-left text-xs font-medium text-t2">
                <tr>
                  {d.columns.map((c) => (
                    <th key={c.key} onClick={() => clickHeader(c.key)}
                      className={clsx('cursor-pointer select-none whitespace-nowrap px-3 py-2 hover:text-t1', isNum(c) && 'text-right')}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {d.sort === c.key && (d.dir === 'desc' ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.columns.some((c) => c.from === 'metrics' || c.total) && (
                  <tr className="border-t border-bd bg-bg3/60 font-medium">
                    {d.columns.map((c, i) => (
                      <td key={c.key} className={clsx('whitespace-nowrap px-3 py-1.5', isNum(c) && 'num text-right')}>
                        {i === 0 ? `Total${d.metrics_unknown ? ` (${d.metrics_unknown} not known)` : ''}`
                          : c.total || c.from === 'derived' ? fmt(d.totals[c.key], c.type, currency) : ''}
                      </td>
                    ))}
                  </tr>
                )}
                {d.rows.map((row) => (
                  <tr key={row.id} className="border-t border-bd hover:bg-hover">
                    {d.columns.map((c, i) => {
                      const raw = METRIC_COLS.has(c.key) && !row.metrics_known ? null : row[c.key];
                      const text = fmt(raw, c.type, currency);
                      return (
                        <td key={c.key} title={c.wide || i === 0 ? String(raw ?? '') : undefined}
                          className={clsx('px-3 py-1.5', isNum(c) ? 'num whitespace-nowrap text-right' : 'max-w-xs truncate')}>
                          {i === 0 ? (
                            <button className="max-w-xs truncate text-left font-medium hover:underline"
                              onClick={() => (drill ? onDrill(drill, row) : setOpen(row))}>{text}</button>
                          ) : c.type === 'grade' ? <GradeBadge grade={row.grade as Grade | null} /> : text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {open && <Details row={open} columns={d?.columns ?? []} currency={currency} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Fields no table has a column for, named and typed for the details panel. */
const EXTRA_LABEL: Record<string, [string, Column['type']]> = {
  grade: ['Mark', 'text'],
  efficiency: ['Efficiency (1.00 = account average)', 'ratio'],
  roas: ['ROAS (conv. value ÷ cost)', 'percent'],
  target_roas: ['Target ROAS', 'percent'],
  optimization_score: ['Optimisation score', 'percent'],
  lost_is_budget: ['Lost impr. share (budget)', 'percent'],
  lost_is_rank: ['Lost impr. share (rank)', 'percent'],
  top_is: ['Top impr. share', 'percent'],
  abs_top_is: ['Absolute top impr. share', 'percent'],
  search_is: ['Search impr. share', 'percent'],
};

const isNum = (c: Column) => c.type === 'number' || c.type === 'money' || c.type === 'percent' || c.type === 'ratio';

/** Every field of one row — including the ones no column has room for. */
function Details({ row, columns, currency, onClose }: { row: Row; columns: Column[]; currency: string; onClose: () => void }) {
  const typed = new Map(columns.map((c) => [c.key, c.type]));
  const skip = new Set(['id', 'metrics_known', 'campaign_id', 'ad_group_id']);
  // A count beside the list it counts says the same thing twice.
  for (const k of Object.keys(row)) if (k.endsWith('_count') && Array.isArray(row[k.replace(/_count$/, 's')])) skip.add(k);
  const entries = Object.entries(row).filter(([k, v]) => !skip.has(k) && v != null && v !== '' && !(Array.isArray(v) && !v.length));
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-20 flex justify-end bg-black/30" onClick={onClose}>
      <aside role="dialog" aria-label="Details" onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-md overflow-y-auto border-l border-bd bg-bg2 p-5 shadow-card">
        <div className="mb-4 flex items-start gap-3">
          <h3 className="mr-auto break-words text-base font-bold">{row.name ?? '—'}</h3>
          <button onClick={onClose} className="text-t3 hover:text-t1" aria-label="Close"><X className="size-4" /></button>
        </div>
        <dl className="space-y-2 text-sm">
          {entries.map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-t3">{columns.find((c) => c.key === k)?.label ?? EXTRA_LABEL[k]?.[0] ?? humanise(k)}</dt>
              <dd className="break-words">
                {Array.isArray(v)
                  ? <ul className="list-disc pl-4">{v.map((x, i) => <li key={i}>{String(x)}</li>)}</ul>
                  : fmt(v, typed.get(k) ?? EXTRA_LABEL[k]?.[1] ?? (typeof v === 'boolean' ? 'bool' : typeof v === 'number' && /cost|cpc|cpa|value|bid|budget|amount/.test(k) ? 'money' : typeof v === 'number' ? 'number' : 'text'), currency)}
              </dd>
            </div>
          ))}
        </dl>
        {!row.metrics_known && <p className="mt-4 text-xs text-t3">Its numbers for this window could not be read from Google.</p>}
      </aside>
    </div>
  );
}
